import React, { useState, useRef, useEffect } from "react";
import { StorageService } from "../../services/storage.js";
import { Logo } from "../common/Logo.js";
import {
  saveImageToUploadFolder,
  resolveImageUrl,
  PRESET_UPLOAD_PHOTOS,
  PRESET_TEACHER_PHOTOS,
  DEFAULT_FALLBACK_IMAGE
} from "../../utils/imageStorage.js";
import {
  FileText,
  Save,
  UploadCloud,
  Upload,
  Image as ImageIcon,
  Sparkles,
  Building2,
  CheckCircle2,
  Trash2,
  GraduationCap,
  Edit3,
  X,
  UserPlus,
  ArrowUp,
  ArrowDown,
  Database
} from "lucide-react";
export const AdminPages = ({
  pages,
  settings: initialSettings,
  currentUser,
  onPageUpdated
}) => {
  const [selectedSlug, setSelectedSlug] = useState("home");
  const [activePage, setActivePage] = useState(() => {
    return pages.find((p) => p.slug === "home") || pages[0];
  });
  const [siteSettings, setSiteSettings] = useState(() => {
    return initialSettings || StorageService.getSettings();
  });
  const [photoSavedSuccess, setPhotoSavedSuccess] = useState(null);
  const [contentSavedSuccess, setContentSavedSuccess] = useState(null);
  const [officeSavedSuccess, setOfficeSavedSuccess] = useState(null);
  const [teacherSavedSuccess, setTeacherSavedSuccess] = useState(null);
  const [imageTick, setImageTick] = useState(0);
  const [activePhotoKey, setActivePhotoKey] = useState(null);
  const [isDraggingPhoto, setIsDraggingPhoto] = useState(false);
  const photoInputRef = useRef(null);
  const [isAddingTeacher, setIsAddingTeacher] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [teacherFormData, setTeacherFormData] = useState({
    name: "",
    name_bn: "",
    designation: "",
    designation_bn: "",
    department: "Administration & Hifz",
    department_bn: "\u09AA\u09CD\u09B0\u09B6\u09BE\u09B8\u09A8 \u0993 \u09A4\u09BE\u09B9\u09AB\u09BF\u099C\u09C1\u09B2 \u0995\u09C1\u09B0\u0986\u09A8",
    qualification: "",
    qualification_bn: "",
    photo: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
    bio: "",
    bio_bn: "",
    phone: "",
    email: "",
    is_active: true
  });
  const teacherPhotoInputRef = useRef(null);
  const canEdit = currentUser.role === "super_admin" || currentUser.role === "principal" || currentUser.role === "editor";
  const [isSavingModalOpen, setIsSavingModalOpen] = useState(false);
  const [savingModalTitle, setSavingModalTitle] = useState("Saving Changes...");
  const [savingModalSubtitle, setSavingModalSubtitle] = useState("Syncing content and photo metadata with MySQL database server...");
  useEffect(() => {
    const handleReady = () => setImageTick((prev) => prev + 1);
    window.addEventListener("bmm-images-ready", handleReady);
    return () => window.removeEventListener("bmm-images-ready", handleReady);
  }, []);
  const handleSelectPage = (slug) => {
    setSelectedSlug(slug);
    const p = pages.find((item) => item.slug === slug);
    if (p) {
      setActivePage({ ...p });
    }
  };
  const handleSavePhotos = (e) => {
    if (e) e.preventDefault();
    if (!canEdit) return;
    setSavingModalTitle(`Saving Photos for "/${activePage.slug}"...`);
    setSavingModalSubtitle("Uploading image assets, validating image paths, and persisting to MySQL database...");
    setIsSavingModalOpen(true);
    setTimeout(() => {
      StorageService.savePage(activePage);
      setIsSavingModalOpen(false);
      setPhotoSavedSuccess(`\u2713 All photos for page "/${activePage.slug}" successfully saved to MySQL database!`);
      onPageUpdated();
      setTimeout(() => setPhotoSavedSuccess(null), 5e3);
    }, 900);
  };
  const handleSaveContent = (e) => {
    if (e) e.preventDefault();
    if (!canEdit) return;
    setSavingModalTitle(`Saving Text Content & SEO for "/${activePage.slug}"...`);
    setSavingModalSubtitle("Optimizing metadata, updating titles and subtitles in MySQL pages table...");
    setIsSavingModalOpen(true);
    setTimeout(() => {
      StorageService.savePage(activePage);
      setIsSavingModalOpen(false);
      setContentSavedSuccess(`\u2713 Content & SEO texts for page "/${activePage.slug}" successfully saved to MySQL database!`);
      onPageUpdated();
      setTimeout(() => setContentSavedSuccess(null), 5e3);
    }, 900);
  };
  const handleSaveCampusOffice = (e) => {
    if (e) e.preventDefault();
    if (!canEdit) return;
    setSavingModalTitle("Saving Campus Office & Location Details...");
    setSavingModalSubtitle("Updating headquarters address, contact numbers, and footer info in MySQL settings...");
    setIsSavingModalOpen(true);
    setTimeout(() => {
      StorageService.saveSettings(siteSettings);
      setIsSavingModalOpen(false);
      setOfficeSavedSuccess("\u2713 Campus Office details and Footer content successfully saved to MySQL settings table!");
      onPageUpdated();
      setTimeout(() => setOfficeSavedSuccess(null), 5e3);
    }, 900);
  };
  const [logoSavedSuccess, setLogoSavedSuccess] = useState(null);
  const logoInputRef = useRef(null);
  const handleLogoFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const path = await saveImageToUploadFolder(file, "common");
    const updatedSettings = { ...siteSettings, logo_url: path };
    setSiteSettings(updatedSettings);
    StorageService.saveSettings(updatedSettings);
    if (onPageUpdated) onPageUpdated();
  };
  const handleSaveLogo = (e) => {
    if (e) e.preventDefault();
    if (!canEdit) return;
    setSavingModalTitle("Saving Brand Logo & Identity...");
    setSavingModalSubtitle("Updating header logo URL and institution identity in MySQL settings table...");
    setIsSavingModalOpen(true);
    setTimeout(() => {
      StorageService.saveSettings(siteSettings);
      setIsSavingModalOpen(false);
      setLogoSavedSuccess("\u2713 Site logo and brand identity successfully saved to MySQL settings table!");
      onPageUpdated();
      setTimeout(() => setLogoSavedSuccess(null), 5e3);
    }, 900);
  };
  const handleSaveTeachers = (e) => {
    if (e) e.preventDefault();
    if (!canEdit) return;
    setSavingModalTitle("Saving Faculty & Scholar Profiles...");
    setSavingModalSubtitle("Persisting faculty member qualifications, photos, and designations in MySQL database...");
    setIsSavingModalOpen(true);
    setTimeout(() => {
      StorageService.savePage(activePage);
      setIsSavingModalOpen(false);
      setTeacherSavedSuccess(`\u2713 All ${activePage.teachers?.length || 0} Faculty Profiles successfully saved to MySQL database!`);
      onPageUpdated();
      setTimeout(() => setTeacherSavedSuccess(null), 5e3);
    }, 900);
  };
  const getPagePhotos = () => {
    if (activePage.photos && activePage.photos.length > 0) {
      return activePage.photos;
    }
    if (activePage.slug === "home") {
      return [
        {
          id: "p-1",
          key: "hero",
          label: "Main Campus Hero Banner",
          label_bn: "\u09AE\u09C2\u09B2 \u0995\u09CD\u09AF\u09BE\u09AE\u09CD\u09AA\u09BE\u09B8 \u09AC\u09CD\u09AF\u09BE\u09A8\u09BE\u09B0 \u099B\u09AC\u09BF",
          url: activePage.hero_image || "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
          caption: "Baitul Muqaddas Model Madrasah Campus"
        },
        {
          id: "p-2",
          key: "campus",
          label: "Campus Grounds & Mosque Minaret",
          label_bn: "\u0995\u09CD\u09AF\u09BE\u09AE\u09CD\u09AA\u09BE\u09B8 \u09AD\u09AC\u09A8 \u0993 \u09AE\u09BF\u09A8\u09BE\u09B0",
          url: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
          caption: "Peaceful learning environment in Daganbhuiyan"
        },
        {
          id: "p-3",
          key: "classroom",
          label: "Classroom & Study Sessions",
          label_bn: "\u09B6\u09CD\u09B0\u09C7\u09A3\u09BF\u0995\u0995\u09CD\u09B7 \u0993 \u09B6\u09BF\u0995\u09CD\u09B7\u09BE\u09B0\u09CD\u09A5\u09C0\u09A6\u09C7\u09B0 \u09AA\u09BE\u09A0\u09A6\u09BE\u09A8",
          url: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
          caption: "Students practicing lessons with care"
        },
        {
          id: "p-4",
          key: "curriculum",
          label: "Textbooks & Calligraphy Slates",
          label_bn: "\u09B8\u09AE\u09A8\u09CD\u09AC\u09BF\u09A4 \u09AA\u09BE\u09A0\u09CD\u09AF\u09AC\u0987 \u0993 \u0995\u09CD\u09AF\u09BE\u09B2\u09BF\u0997\u09CD\u09B0\u09BE\u09AB\u09BF \u09B8\u09CD\u09B2\u09C7\u099F",
          url: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
          caption: "Traditional slates, Arabic calligraphy & textbooks"
        },
        {
          id: "p-5",
          key: "teachers",
          label: "Faculty & Asatizah Scholars",
          label_bn: "\u09B8\u09AE\u09CD\u09AE\u09BE\u09A8\u09BF\u09A4 \u09B6\u09BF\u0995\u09CD\u09B7\u0995\u09AE\u09A3\u09CD\u09A1\u09B2\u09C0 \u0993 \u09B6\u09BF\u0995\u09CD\u09B7\u0995 \u09B8\u09AE\u09CD\u09AE\u09C7\u09B2\u09A8",
          url: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
          caption: "Dedicated scholars guiding young learners"
        }
      ];
    }
    if (activePage.slug === "about") {
      return [
        {
          id: "p-ab-1",
          key: "hero",
          label: "About Header Banner Photo",
          label_bn: "\u09AA\u09B0\u09BF\u099A\u09BF\u09A4\u09BF \u09AA\u09BE\u09A4\u09BE \u09AC\u09CD\u09AF\u09BE\u09A8\u09BE\u09B0 \u099B\u09AC\u09BF",
          url: activePage.hero_image || "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
          caption: "Faculty and scholars of Baitul Muqaddas"
        },
        {
          id: "p-ab-2",
          key: "teachers",
          label: "Faculty & Teachers Group",
          label_bn: "\u09B6\u09BF\u0995\u09CD\u09B7\u0995\u09AE\u09A3\u09CD\u09A1\u09B2\u09C0 \u0993 \u09AA\u09CD\u09B0\u09B6\u09BE\u09B8\u09A8\u09BF\u0995 \u099F\u09BF\u09AE",
          url: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
          caption: "Our respected Asatizah"
        },
        {
          id: "p-ab-3",
          key: "campus",
          label: "Campus Grounds & Environment",
          label_bn: "\u0995\u09CD\u09AF\u09BE\u09AE\u09CD\u09AA\u09BE\u09B8 \u09AA\u09CD\u09B0\u09BE\u0999\u09CD\u0997\u09A3 \u0993 \u09AA\u09B0\u09BF\u09AC\u09C7\u09B6",
          url: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
          caption: "Arched architecture and lush green campus"
        }
      ];
    }
    if (activePage.slug === "curriculum") {
      return [
        {
          id: "p-cur-1",
          key: "hero",
          label: "Curriculum Banner Photo",
          label_bn: "\u0995\u09BE\u09B0\u09BF\u0995\u09C1\u09B2\u09BE\u09AE \u09AC\u09CD\u09AF\u09BE\u09A8\u09BE\u09B0 \u099B\u09AC\u09BF",
          url: activePage.hero_image || "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
          caption: "Dual stream academic textbooks & sacred texts"
        },
        {
          id: "p-cur-2",
          key: "books",
          label: "Books & Traditional Calligraphy Slates",
          label_bn: "\u09AA\u09BE\u09A0\u09CD\u09AF\u09AC\u0987 \u0993 \u099A\u0995-\u09B8\u09CD\u09B2\u09C7\u099F \u0995\u09CD\u09AF\u09BE\u09B2\u09BF\u0997\u09CD\u09B0\u09BE\u09AB\u09BF",
          url: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
          caption: "Practicing Arabic and English handwriting"
        },
        {
          id: "p-cur-3",
          key: "classroom",
          label: "Classroom Study Instruction",
          label_bn: "\u09B6\u09CD\u09B0\u09C7\u09A3\u09BF\u0995\u0995\u09CD\u09B7\u09C7 \u09B8\u09B0\u09BE\u09B8\u09B0\u09BF \u09AA\u09BE\u09A0\u09A6\u09BE\u09A8",
          url: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
          caption: "Individual student mentorship"
        }
      ];
    }
    if (activePage.slug === "blog") {
      return [
        {
          id: "p-bl-1",
          key: "hero",
          label: "Blog & Announcements Banner Photo",
          label_bn: "\u09AC\u09CD\u09B2\u0997 \u0993 \u09A8\u09CB\u099F\u09BF\u09B6 \u09AC\u09CD\u09AF\u09BE\u09A8\u09BE\u09B0 \u099B\u09AC\u09BF",
          url: activePage.hero_image || "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
          caption: "Publications and scholastic updates"
        },
        {
          id: "p-bl-2",
          key: "campus",
          label: "Campus Events Photo",
          label_bn: "\u0995\u09CD\u09AF\u09BE\u09AE\u09CD\u09AA\u09BE\u09B8 \u0995\u09BE\u09B0\u09CD\u09AF\u0995\u09CD\u09B0\u09AE \u0993 \u0985\u09A8\u09C1\u09B7\u09CD\u09A0\u09BE\u09A8",
          url: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
          caption: "Annual gathering and student awards"
        }
      ];
    }
    return [
      {
        id: "p-ct-1",
        key: "hero",
        label: "Contact Page Hero Banner",
        label_bn: "\u09AF\u09CB\u0997\u09BE\u09AF\u09CB\u0997 \u09AA\u09BE\u09A4\u09BE \u09AC\u09CD\u09AF\u09BE\u09A8\u09BE\u09B0 \u099B\u09AC\u09BF",
        url: activePage.hero_image || "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
        caption: "Main administrative entrance & visiting area"
      },
      {
        id: "p-ct-2",
        key: "office",
        label: "Campus Administrative Office Desk",
        label_bn: "\u0995\u09CD\u09AF\u09BE\u09AE\u09CD\u09AA\u09BE\u09B8 \u0985\u09AB\u09BF\u09B8 \u0993 \u0985\u09AD\u09CD\u09AF\u09B0\u09CD\u09A5\u09A8\u09BE \u09A1\u09C7\u0995\u09CD\u09B8",
        url: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
        caption: "Office reception and admission inquiry desk"
      }
    ];
  };
  const currentPhotos = getPagePhotos();
  const handleUpdatePhoto = (photoKey, newUrl) => {
    const updatedPhotos = currentPhotos.map((p) => {
      if (p.key === photoKey) {
        return { ...p, url: newUrl };
      }
      return p;
    });
    const isHero = photoKey === "hero";
    const updatedPage = {
      ...activePage,
      hero_image: isHero ? newUrl : activePage.hero_image,
      photos: updatedPhotos
    };
    setActivePage(updatedPage);
    StorageService.savePage(updatedPage);
    if (onPageUpdated) onPageUpdated();
  };
  const handleFileProcess = (file, photoKey) => {
    if (!file.type.startsWith("image/")) return;
    const cleanPath = saveImageToUploadFolder(file, "pages", activePage.slug);
    handleUpdatePhoto(photoKey, cleanPath);
  };
  const handleDrop = (e, photoKey) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPhoto(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileProcess(e.dataTransfer.files[0], photoKey);
    }
  };
  const handleOpenAddTeacher = () => {
    setTeacherFormData({
      name: "",
      name_bn: "",
      designation: "",
      designation_bn: "",
      department: "Islamic Studies & Arabic",
      department_bn: "\u0987\u09B8\u09B2\u09BE\u09AE\u09BF\u09DF\u09BE\u09A4 \u0993 \u0986\u09B0\u09AC\u09BF \u09AD\u09BE\u09B7\u09BE",
      qualification: "",
      qualification_bn: "",
      photo: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
      bio: "",
      bio_bn: "",
      phone: "",
      email: "",
      is_active: true
    });
    setEditingTeacher(null);
    setIsAddingTeacher(true);
  };
  const handleOpenEditTeacher = (teacher) => {
    setTeacherFormData({ ...teacher });
    setEditingTeacher(teacher);
    setIsAddingTeacher(true);
  };
  const handleTeacherPhotoFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.type.startsWith("image/")) {
        const cleanPath = saveImageToUploadFolder(file, "pages", "teachers");
        setTeacherFormData((prev) => ({ ...prev, photo: cleanPath }));
      }
    }
  };
  const handleSaveTeacherModalSubmit = (e) => {
    e.preventDefault();
    if (!teacherFormData.name?.trim() || !teacherFormData.name_bn?.trim()) {
      alert("Please fill in teacher names in both English and Bengali.");
      return;
    }
    setSavingModalTitle("Saving Faculty Profile...");
    setSavingModalSubtitle("Updating Asatizah profile records and syncing with MySQL database...");
    setIsSavingModalOpen(true);
    setTimeout(() => {
      const currentTeachers = activePage.teachers ? [...activePage.teachers] : [];
      let updatedTeachers = currentTeachers;
      if (editingTeacher) {
        updatedTeachers = currentTeachers.map((t) => {
          if (t.id === editingTeacher.id) {
            return {
              ...t,
              ...teacherFormData,
              id: editingTeacher.id
            };
          }
          return t;
        });
      } else {
        const newTeacher = {
          id: `tch-${Date.now()}`,
          name: teacherFormData.name?.trim() || "",
          name_bn: teacherFormData.name_bn?.trim() || "",
          designation: teacherFormData.designation?.trim() || "Teacher",
          designation_bn: teacherFormData.designation_bn?.trim() || "\u09B6\u09BF\u0995\u09CD\u09B7\u0995",
          department: teacherFormData.department || "Islamic Studies",
          department_bn: teacherFormData.department_bn || "\u0987\u09B8\u09B2\u09BE\u09AE\u09BF \u09B6\u09BF\u0995\u09CD\u09B7\u09BE",
          qualification: teacherFormData.qualification?.trim() || "Dawra-e-Hadith",
          qualification_bn: teacherFormData.qualification_bn?.trim() || "\u09A6\u09BE\u0993\u09B0\u09BE\u09AF\u09BC\u09C7 \u09B9\u09BE\u09A6\u09BF\u09B8",
          photo: teacherFormData.photo || "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
          bio: teacherFormData.bio || "",
          bio_bn: teacherFormData.bio_bn || "",
          phone: teacherFormData.phone || "",
          email: teacherFormData.email || "",
          order: currentTeachers.length + 1,
          is_active: teacherFormData.is_active !== false
        };
        updatedTeachers = [...currentTeachers, newTeacher];
      }
      const updatedPage = { ...activePage, teachers: updatedTeachers };
      setActivePage(updatedPage);
      StorageService.savePage(updatedPage);
      setIsSavingModalOpen(false);
      setIsAddingTeacher(false);
      setEditingTeacher(null);
      setTeacherSavedSuccess(`\u2713 Faculty Profile successfully saved to MySQL database!`);
      onPageUpdated();
      setTimeout(() => setTeacherSavedSuccess(null), 5e3);
    }, 900);
  };
  const handleDeleteTeacher = (id) => {
    if (!window.confirm("Are you sure you want to remove this teacher profile?")) return;
    const currentTeachers = activePage.teachers ? [...activePage.teachers] : [];
    const updated = currentTeachers.filter((t) => t.id !== id);
    setActivePage((prev) => ({ ...prev, teachers: updated }));
  };
  const handleToggleTeacherActive = (id) => {
    const currentTeachers = activePage.teachers ? [...activePage.teachers] : [];
    const updated = currentTeachers.map((t) => {
      if (t.id === id) {
        return { ...t, is_active: !t.is_active };
      }
      return t;
    });
    setActivePage((prev) => ({ ...prev, teachers: updated }));
  };
  const handleMoveTeacherUp = (index) => {
    if (index <= 0) return;
    const currentTeachers = activePage.teachers ? [...activePage.teachers] : [];
    const temp = currentTeachers[index];
    currentTeachers[index] = currentTeachers[index - 1];
    currentTeachers[index - 1] = temp;
    setActivePage((prev) => ({ ...prev, teachers: currentTeachers }));
  };
  const handleMoveTeacherDown = (index) => {
    const currentTeachers = activePage.teachers ? [...activePage.teachers] : [];
    if (index >= currentTeachers.length - 1) return;
    const temp = currentTeachers[index];
    currentTeachers[index] = currentTeachers[index + 1];
    currentTeachers[index + 1] = temp;
    setActivePage((prev) => ({ ...prev, teachers: currentTeachers }));
  };
  return <div className="space-y-8 font-sans">
      {
    /* Top Page Header */
  }
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-serif font-bold text-slate-900 tracking-tight">
            Site Pages Content & Photos Manager
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Manage all photos, text copy, faculty profiles, and Campus Office details across all 5 core pages.
          </p>
        </div>
      </div>

      {
    /* Page Tabs */
  }
      <div className="flex flex-wrap gap-2 border-b border-stone-200 pb-3">
        {pages.map((p) => {
    const isSelected = selectedSlug === p.slug;
    return <button
      key={p.id}
      onClick={() => handleSelectPage(p.slug)}
      className={`px-4 py-2 text-xs font-semibold rounded-xl transition-all cursor-pointer flex items-center gap-2 ${isSelected ? "bg-emerald-950 text-white shadow-sm" : "bg-white text-slate-700 border border-stone-200 hover:bg-stone-50"}`}
    >
              <FileText className="w-3.5 h-3.5" />
              <span>{p.title} ({p.title_bn})</span>
            </button>;
  })}
      </div>

      {
    /* ------------------------------------------------------------------ */
  }
      {
    /* SECTION 1: ALL PHOTOS FOR CURRENT PAGE WITH SEPARATE SAVE BUTTON */
  }
      {
    /* ------------------------------------------------------------------ */
  }
      <div className="bg-white p-6 sm:p-8 rounded-2xl border border-stone-200 shadow-xs space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-stone-200 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-emerald-100 text-emerald-900">
                <ImageIcon className="w-4 h-4" />
              </span>
              <h2 className="text-base font-bold text-slate-900">
                Photos for Page: /{activePage.slug} ({currentPhotos.length} Photos)
              </h2>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              All photos displayed on this page are listed below. Upload, drag-and-drop, or select presets to change them.
            </p>
          </div>

          {canEdit && <button
    type="button"
    onClick={() => handleSavePhotos()}
    className="px-5 py-2 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer self-start sm:self-auto"
  >
              <Save className="w-4 h-4" />
              <span>Save Page Photos</span>
            </button>}
        </div>

        {photoSavedSuccess && <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-semibold flex items-center gap-2 animate-in fade-in">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{photoSavedSuccess}</span>
          </div>}

        {
    /* Photos Grid for This Page */
  }
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {currentPhotos.map((photo) => {
    const isHero = photo.key === "hero";
    return <div
      key={photo.id}
      className="p-4 bg-stone-50 rounded-xl border border-stone-200 space-y-3 flex flex-col justify-between"
    >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <span>{photo.label}</span>
                      <span className="text-[11px] text-slate-500 font-normal font-bengali">({photo.label_bn})</span>
                    </span>
                    {isHero && <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-amber-400 text-emerald-950 uppercase tracking-wider">
                        Primary Banner
                      </span>}
                  </div>

                  {
      /* Photo Preview */
    }
                  <div className="h-44 rounded-xl overflow-hidden bg-slate-900 border border-stone-300 relative group shadow-inner">
                    <img
      key={`${photo.key}-${photo.url}-${imageTick}`}
      src={resolveImageUrl(photo.url)}
      alt={photo.label}
      className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-300"
      referrerPolicy="no-referrer"
      onError={(e) => {
        e.currentTarget.src = DEFAULT_FALLBACK_IMAGE;
      }}
    />
                    <div className="absolute bottom-2 left-2 bg-black/75 text-white text-[10px] font-mono px-2 py-0.5 rounded backdrop-blur-xs max-w-[90%] truncate">
                      {photo.url}
                    </div>
                  </div>
                </div>

                {
      /* Upload & Path Controls */
    }
                <div className="space-y-2 pt-2 border-t border-stone-200">
                  {
      /* Drag & Drop mini dropzone */
    }
                  <div
      onDragOver={(e) => {
        e.preventDefault();
        setIsDraggingPhoto(true);
      }}
      onDragLeave={() => setIsDraggingPhoto(false)}
      onDrop={(e) => handleDrop(e, photo.key)}
      className={`border border-dashed ${isDraggingPhoto ? "border-emerald-700 bg-emerald-50" : "border-stone-300 bg-white"} hover:border-emerald-700 rounded-lg p-2.5 text-center cursor-pointer transition-colors`}
      onClick={() => {
        setActivePhotoKey(photo.key);
        photoInputRef.current?.click();
      }}
    >
                    <div className="flex items-center justify-center gap-2 text-xs text-slate-700">
                      <UploadCloud className="w-4 h-4 text-emerald-800" />
                      <span className="font-semibold text-[11px]">
                        Drop photo here or <span className="text-emerald-800 underline">browse file</span>
                      </span>
                    </div>
                  </div>

                  {
      /* Preset Selector */
    }
                  <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
                    <span className="text-slate-500 font-medium">Quick Presets:</span>
                    {PRESET_UPLOAD_PHOTOS.map((pr, idx) => <button
      key={idx}
      type="button"
      onClick={() => handleUpdatePhoto(photo.key, pr.path)}
      className={`px-2 py-0.5 rounded border transition-colors cursor-pointer ${photo.url === pr.path ? "bg-emerald-900 text-white border-emerald-950 font-bold" : "bg-white text-slate-700 border-stone-200 hover:bg-stone-100"}`}
    >
                        {pr.name.split("&")[0]}
                      </button>)}
                  </div>

                  {
      /* Direct Path Input */
    }
                  <div className="flex items-center gap-1.5 pt-1">
                    <input
      type="text"
      value={photo.url}
      onChange={(e) => handleUpdatePhoto(photo.key, e.target.value)}
      placeholder="/assets/images/uploads/..."
      className="flex-1 px-2.5 py-1 text-[11px] rounded-lg border border-stone-200 bg-white font-mono text-slate-700"
    />
                  </div>
                </div>
              </div>;
  })}
        </div>

        {
    /* Hidden File Input used by photo dropzones */
  }
        <input
    type="file"
    accept="image/*"
    ref={photoInputRef}
    onChange={(e) => {
      if (e.target.files && e.target.files.length > 0 && activePhotoKey) {
        handleFileProcess(e.target.files[0], activePhotoKey);
      }
    }}
    className="hidden"
  />

        <div className="flex justify-end pt-2">
          {canEdit && <button
    type="button"
    onClick={() => handleSavePhotos()}
    className="px-5 py-2 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer"
  >
              <Save className="w-4 h-4" />
              <span>Save Page Photos</span>
            </button>}
        </div>
      </div>

      {
    /* ---------------------------------------------------------------------------------- */
  }
      {
    /* SECTION 2: DYNAMIC TEACHER & FACULTY PROFILES MANAGER (SPECIFICALLY FOR ABOUT PAGE) */
  }
      {
    /* ---------------------------------------------------------------------------------- */
  }
      {activePage.slug === "about" && <div className="bg-white p-6 sm:p-8 rounded-2xl border border-stone-200 shadow-xs space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-stone-200 pb-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="p-1.5 rounded-lg bg-amber-100 text-amber-900">
                  <GraduationCap className="w-4 h-4 text-amber-800" />
                </span>
                <h2 className="text-base font-bold text-slate-900">
                  Faculty & Teacher Profiles Manager (About Page)
                </h2>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Add, edit, change photos, and organize the teachers and scholars displayed on the public About page.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
              {canEdit && <>
                  <button
    type="button"
    onClick={handleOpenAddTeacher}
    className="px-4 py-2 text-xs font-bold text-emerald-950 bg-amber-400 hover:bg-amber-300 rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer"
  >
                    <UserPlus className="w-4 h-4" />
                    <span>Add New Teacher</span>
                  </button>

                  <button
    type="button"
    onClick={() => handleSaveTeachers()}
    className="px-5 py-2 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer"
  >
                    <Save className="w-4 h-4" />
                    <span>Save Teacher Profiles</span>
                  </button>
                </>}
            </div>
          </div>

          {teacherSavedSuccess && <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-semibold flex items-center gap-2 animate-in fade-in">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>{teacherSavedSuccess}</span>
            </div>}

          {
    /* Teacher Cards Grid in Admin */
  }
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {(activePage.teachers || []).map((teacher, index) => <div
    key={teacher.id}
    className={`p-4 rounded-2xl border space-y-3 relative flex flex-col justify-between transition-all ${teacher.is_active !== false ? "bg-stone-50 border-stone-200" : "bg-stone-100/60 border-stone-300 opacity-60"}`}
  >
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-900 shrink-0 border border-stone-300 relative shadow-sm">
                      <img
    src={resolveImageUrl(teacher.photo)}
    alt={teacher.name}
    className="w-full h-full object-cover"
    referrerPolicy="no-referrer"
    onError={(e) => {
      if (e.currentTarget.dataset.failed) return;
      e.currentTarget.dataset.failed = "true";
      e.currentTarget.src = DEFAULT_FALLBACK_IMAGE;
    }}
  />
                    </div>

                    <div className="flex-1 min-w-0">
                      <span className="inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-900 truncate max-w-full">
                        {teacher.department_bn || teacher.department}
                      </span>
                      <h4 className="text-xs font-bold text-slate-900 truncate">
                        {teacher.name}
                      </h4>
                      <p className="text-[11px] text-slate-600 truncate font-bengali">
                        {teacher.name_bn}
                      </p>
                      <p className="text-[10px] text-amber-800 font-semibold truncate">
                        {teacher.designation_bn || teacher.designation}
                      </p>
                    </div>
                  </div>

                  <div className="text-[11px] space-y-1 bg-white p-2.5 rounded-xl border border-stone-200">
                    <div>
                      <strong className="text-slate-500 font-normal">Qualification: </strong>
                      <span className="text-slate-800 font-medium">{teacher.qualification_bn || teacher.qualification}</span>
                    </div>
                    {teacher.phone && <div>
                        <strong className="text-slate-500 font-normal">Phone: </strong>
                        <span className="text-slate-700 font-mono">{teacher.phone}</span>
                      </div>}
                  </div>
                </div>

                {
    /* Actions */
  }
                <div className="pt-2 border-t border-stone-200 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <button
    type="button"
    onClick={() => handleToggleTeacherActive(teacher.id)}
    className={`px-2 py-1 text-[10px] font-bold rounded cursor-pointer ${teacher.is_active !== false ? "bg-emerald-100 text-emerald-800" : "bg-stone-200 text-slate-600"}`}
  >
                      {teacher.is_active !== false ? "Active" : "Inactive"}
                    </button>

                    {
    /* Order up/down */
  }
                    <button
    type="button"
    disabled={index === 0}
    onClick={() => handleMoveTeacherUp(index)}
    className="p-1 rounded text-slate-400 hover:text-slate-700 disabled:opacity-20 cursor-pointer"
    title="Move Up"
  >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
    type="button"
    disabled={index === (activePage.teachers?.length || 1) - 1}
    onClick={() => handleMoveTeacherDown(index)}
    className="p-1 rounded text-slate-400 hover:text-slate-700 disabled:opacity-20 cursor-pointer"
    title="Move Down"
  >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
    type="button"
    onClick={() => handleOpenEditTeacher(teacher)}
    className="p-1.5 rounded-lg text-slate-600 hover:text-emerald-800 hover:bg-stone-200 cursor-pointer"
    title="Edit Teacher"
  >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
    type="button"
    onClick={() => handleDeleteTeacher(teacher.id)}
    className="p-1.5 rounded-lg text-red-600 hover:text-red-800 hover:bg-red-50 cursor-pointer"
    title="Delete Teacher"
  >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>)}
          </div>

          <div className="flex justify-end pt-2">
            {canEdit && <button
    type="button"
    onClick={() => handleSaveTeachers()}
    className="px-5 py-2 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer"
  >
                <Save className="w-4 h-4" />
                <span>Save Teacher Profiles</span>
              </button>}
          </div>
        </div>}

      {
    /* ------------------------------------------------------------------ */
  }
      {
    /* SITE LOGO & BRAND IDENTITY MANAGER                                 */
  }
      {
    /* ------------------------------------------------------------------ */
  }
      <div className="bg-white p-6 sm:p-8 rounded-2xl border border-stone-200 shadow-xs space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-stone-200 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-emerald-100 text-emerald-900">
                <Sparkles className="w-4 h-4" />
              </span>
              <h2 className="text-base font-bold text-slate-900">
                Site Logo & Brand Identity Manager
              </h2>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Upload a custom logo or choose a preset emblem. The logo automatically updates in the header, footer, and admin panel.
            </p>
          </div>

          {canEdit && <button
    type="button"
    onClick={() => handleSaveLogo()}
    className="px-5 py-2 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer self-start sm:self-auto"
  >
              <Save className="w-4 h-4" />
              <span>Save Logo & Branding</span>
            </button>}
        </div>

        {logoSavedSuccess && <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-semibold flex items-center gap-2 animate-in fade-in">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{logoSavedSuccess}</span>
          </div>}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {
    /* Left: Previews in Light & Dark mode */
  }
          <div className="lg:col-span-5 space-y-4">
            <label className="block text-xs font-semibold text-slate-700">
              Live Logo Previews:
            </label>
            <div className="p-4 bg-white rounded-xl border border-stone-200 shadow-xs space-y-3">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Light Header Mode (Navbar)</span>
              <div className="p-3 bg-stone-50 rounded-lg border border-stone-100 flex items-center justify-between">
                <Logo size="md" logoUrl={siteSettings.logo_url} siteName={siteSettings.site_name} theme="light" />
              </div>
            </div>

            <div className="p-4 bg-emerald-950 rounded-xl border border-emerald-900 shadow-xs space-y-3">
              <span className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider block">Dark Footer / Admin Mode</span>
              <div className="p-3 bg-emerald-900/60 rounded-lg border border-emerald-800 flex items-center justify-between">
                <Logo size="md" logoUrl={siteSettings.logo_url} siteName={siteSettings.site_name} theme="dark" />
              </div>
            </div>
          </div>

          {
    /* Right: Upload controls & Presets */
  }
          <div className="lg:col-span-7 space-y-5">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Upload Custom Logo File (PNG, SVG, JPG)
              </label>
              <div className="flex items-center gap-3">
                <input
    type="file"
    ref={logoInputRef}
    accept="image/*"
    onChange={handleLogoFileUpload}
    className="hidden"
  />
                <button
    type="button"
    onClick={() => logoInputRef.current?.click()}
    className="px-4 py-2 text-xs font-bold bg-stone-100 hover:bg-stone-200 text-slate-800 rounded-xl border border-stone-300 cursor-pointer flex items-center gap-1.5"
  >
                  <Upload className="w-3.5 h-3.5 text-emerald-800" />
                  <span>Choose Logo File...</span>
                </button>
                {siteSettings.logo_url && <button
    type="button"
    onClick={() => setSiteSettings({ ...siteSettings, logo_url: "" })}
    className="text-xs font-medium text-rose-600 hover:text-rose-800 cursor-pointer"
  >
                    Reset to Default Vector Emblem
                  </button>}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Or Logo Image URL / Asset Path:
              </label>
              <input
    type="text"
    value={siteSettings.logo_url || ""}
    onChange={(e) => setSiteSettings({ ...siteSettings, logo_url: e.target.value })}
    placeholder="e.g. /assets/images/logo.png or https://..."
    className="w-full px-3 py-2 text-xs font-mono rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>

            {
    /* Quick Logo Presets */
  }
            <div>
              <span className="text-xs font-semibold text-slate-700 block mb-2">
                Quick Preset Emblems:
              </span>
              <div className="flex flex-wrap gap-2">
                <button
    type="button"
    onClick={() => setSiteSettings({ ...siteSettings, logo_url: "" })}
    className={`px-3 py-1.5 text-xs rounded-xl border font-medium cursor-pointer ${!siteSettings.logo_url ? "bg-emerald-900 text-white border-emerald-900" : "bg-stone-50 text-slate-700 border-stone-200 hover:bg-stone-100"}`}
  >
                  Default Vector Dome & Quran
                </button>
                <button
    type="button"
    onClick={() => setSiteSettings({ ...siteSettings, logo_url: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg" })}
    className="px-3 py-1.5 text-xs rounded-xl border bg-stone-50 text-slate-700 border-stone-200 hover:bg-stone-100 font-medium cursor-pointer"
  >
                  Golden Crescent Crest
                </button>
                <button
    type="button"
    onClick={() => setSiteSettings({ ...siteSettings, logo_url: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg" })}
    className="px-3 py-1.5 text-xs rounded-xl border bg-stone-50 text-slate-700 border-stone-200 hover:bg-stone-100 font-medium cursor-pointer"
  >
                  Calligraphic Badge
                </button>
              </div>
            </div>

            {
    /* Site Name Editing */
  }
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-stone-100">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Site Name (English) *
                </label>
                <input
    type="text"
    value={siteSettings.site_name}
    onChange={(e) => setSiteSettings({ ...siteSettings, site_name: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  প্রতিষ্ঠানের নাম (বাংলা) *
                </label>
                <input
    type="text"
    value={siteSettings.site_name_bn}
    onChange={(e) => setSiteSettings({ ...siteSettings, site_name_bn: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-bengali focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
              </div>
            </div>{canEdit && <button
    type="button"
    onClick={() => handleSaveLogo()}
    className="px-5 py-2 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer self-start sm:self-auto"
  >
              <Save className="w-4 h-4" />
              <span>Save Logo & Branding</span>
            </button>}
          </div>
        </div>
      </div>

      {
    /* ------------------------------------------------------------------ */
  }
      {
    /* SECTION 3: PAGE TEXT CONTENT & HEADLINES WITH SEPARATE SAVE BUTTON */
  }
      {
    /* ------------------------------------------------------------------ */
  }
      <div className="bg-white p-6 sm:p-8 rounded-2xl border border-stone-200 shadow-xs space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-stone-200 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-blue-100 text-blue-900">
                <FileText className="w-4 h-4" />
              </span>
              <h2 className="text-base font-bold text-slate-900">
                Page Text Content & Headlines (/{activePage.slug})
              </h2>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Edit English and Bengali headlines, subtitles, and detailed body text for this page.
            </p>
          </div>

          {canEdit && <button
    type="button"
    onClick={() => handleSaveContent()}
    className="px-5 py-2 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer self-start sm:self-auto"
  >
              <Save className="w-4 h-4" />
              <span>Save Page Content</span>
            </button>}
        </div>

        {contentSavedSuccess && <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-semibold flex items-center gap-2 animate-in fade-in">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{contentSavedSuccess}</span>
          </div>}

        <div className="space-y-4">
          {
    /* Page Titles */
  }
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Page Title (English) *
              </label>
              <input
    type="text"
    value={activePage.title}
    onChange={(e) => setActivePage({ ...activePage, title: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                পৃষ্ঠা শিরোনাম (বাংলা) *
              </label>
              <input
    type="text"
    value={activePage.title_bn}
    onChange={(e) => setActivePage({ ...activePage, title_bn: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-bengali focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>
          </div>

          {
    /* Subtitles */
  }
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Header Subtitle / Tagline (English)
              </label>
              <input
    type="text"
    value={activePage.subtitle}
    onChange={(e) => setActivePage({ ...activePage, subtitle: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                উপ-শিরোনাম (বাংলা)
              </label>
              <input
    type="text"
    value={activePage.subtitle_bn}
    onChange={(e) => setActivePage({ ...activePage, subtitle_bn: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-bengali focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>
          </div>

          {
    /* Body Content */
  }
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Main Body Paragraph (English)
              </label>
              <textarea
    rows={4}
    value={activePage.content}
    onChange={(e) => setActivePage({ ...activePage, content: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                মূল বিবরণ ও বার্তা (বাংলা)
              </label>
              <textarea
    rows={4}
    value={activePage.content_bn}
    onChange={(e) => setActivePage({ ...activePage, content_bn: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-bengali focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          {canEdit && <button
    type="button"
    onClick={() => handleSaveContent()}
    className="px-5 py-2 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer"
  >
              <Save className="w-4 h-4" />
              <span>Save Page Content</span>
            </button>}
        </div>
      </div>

      {
    /* ------------------------------------------------------------------ */
  }
      {
    /* SECTION 4: CAMPUS OFFICE & LOCATION CONTENT & FOOTER TEXTS         */
  }
      {
    /* ------------------------------------------------------------------ */
  }
      <div className="bg-white p-6 sm:p-8 rounded-2xl border border-stone-200 shadow-xs space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-stone-200 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-amber-100 text-amber-900">
                <Building2 className="w-4 h-4" />
              </span>
              <h2 className="text-base font-bold text-slate-900">
                Campus Office, Location & Footer Text Manager
              </h2>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Change the Campus Office title, address, visiting hours, contact hotline, and footer text across the entire website.
            </p>
          </div>

          {canEdit && <button
    type="button"
    onClick={() => handleSaveCampusOffice()}
    className="px-5 py-2 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer self-start sm:self-auto"
  >
              <Save className="w-4 h-4" />
              <span>Save Campus Office & Footer</span>
            </button>}
        </div>

        {officeSavedSuccess && <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-semibold flex items-center gap-2 animate-in fade-in">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{officeSavedSuccess}</span>
          </div>}

        <div className="space-y-4">
          {
    /* Campus Office Section Titles */
  }
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Campus Office Title (English) *
              </label>
              <input
    type="text"
    value={siteSettings.campus_office_title || "Campus Office & Visitor Desk"}
    onChange={(e) => setSiteSettings({ ...siteSettings, campus_office_title: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                ক্যাম্পাস অফিস শিরোনাম (বাংলা) *
              </label>
              <input
    type="text"
    value={siteSettings.campus_office_title_bn || "\u0995\u09CD\u09AF\u09BE\u09AE\u09CD\u09AA\u09BE\u09B8 \u0985\u09AB\u09BF\u09B8 \u0993 \u09A4\u09A5\u09CD\u09AF \u0995\u09C7\u09A8\u09CD\u09A6\u09CD\u09B0"}
    onChange={(e) => setSiteSettings({ ...siteSettings, campus_office_title_bn: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-bengali focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>
          </div>

          {
    /* Campus Physical Location Description */
  }
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Campus Location / Building Details (English)
              </label>
              <input
    type="text"
    value={siteSettings.campus_office_location || "Central Academic Building, 1st Floor, Main Campus Gate"}
    onChange={(e) => setSiteSettings({ ...siteSettings, campus_office_location: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                ক্যাম্পাস ভবন ও অবস্থান বিবরণী (বাংলা)
              </label>
              <input
    type="text"
    value={siteSettings.campus_office_location_bn || "\u0995\u09C7\u09A8\u09CD\u09A6\u09CD\u09B0\u09C0\u09AF\u09BC \u098F\u0995\u09BE\u09A1\u09C7\u09AE\u09BF\u0995 \u09AD\u09AC\u09A8, \u09E7\u09AE \u09A4\u09B2\u09BE, \u09AE\u09C2\u09B2 \u09AB\u099F\u0995 \u09B8\u0982\u09B2\u0997\u09CD\u09A8"}
    onChange={(e) => setSiteSettings({ ...siteSettings, campus_office_location_bn: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-bengali focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>
          </div>

          {
    /* Office Visiting Hours */
  }
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Office & Visitor Hours (English)
              </label>
              <input
    type="text"
    value={siteSettings.campus_office_hours || "Saturday \u2013 Thursday: 8:00 AM \u2013 5:00 PM (Friday Closed)"}
    onChange={(e) => setSiteSettings({ ...siteSettings, campus_office_hours: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                অফিস সময়সূচী (বাংলা)
              </label>
              <input
    type="text"
    value={siteSettings.campus_office_hours_bn || "\u09B6\u09A8\u09BF\u09AC\u09BE\u09B0 - \u09AC\u09C3\u09B9\u09B8\u09CD\u09AA\u09A4\u09BF\u09AC\u09BE\u09B0: \u09B8\u0995\u09BE\u09B2 \u09EE:\u09E6\u09E6 - \u09AC\u09BF\u0995\u09BE\u09B2 \u09EB:\u09E6\u09E6 (\u09B6\u09C1\u0995\u09CD\u09B0\u09AC\u09BE\u09B0 \u09AC\u09A8\u09CD\u09A7)"}
    onChange={(e) => setSiteSettings({ ...siteSettings, campus_office_hours_bn: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-bengali focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>
          </div>

          {
    /* Landmark & Directions */
  }
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Landmark & Directions (English)
              </label>
              <input
    type="text"
    value={siteSettings.campus_office_landmark || "Adjacent to Tulatoli Bazar, Daganbhuiyan, Feni (Near Tulatoli Central Jame Masjid)"}
    onChange={(e) => setSiteSettings({ ...siteSettings, campus_office_landmark: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                ল্যান্ডমার্ক ও পৌঁছানোর নির্দেশনা (বাংলা)
              </label>
              <input
    type="text"
    value={siteSettings.campus_office_landmark_bn || "\u09A4\u09C1\u09B2\u09BE\u09A4\u09B2\u09C0 \u09AC\u09BE\u099C\u09BE\u09B0 \u09B8\u0982\u09B2\u0997\u09CD\u09A8, \u09A6\u09BE\u0997\u09A8\u09AD\u09C2\u099E\u09BE, \u09AB\u09C7\u09A8\u09C0 (\u09A4\u09C1\u09B2\u09BE\u09A4\u09B2\u09C0 \u0995\u09C7\u09A8\u09CD\u09A6\u09CD\u09B0\u09C0\u09AF\u09BC \u099C\u09BE\u09AE\u09C7 \u09AE\u09B8\u099C\u09BF\u09A6\u09C7\u09B0 \u09AA\u09BE\u09B0\u09CD\u09B6\u09CD\u09AC\u09C7)"}
    onChange={(e) => setSiteSettings({ ...siteSettings, campus_office_landmark_bn: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-bengali focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>
          </div>

          {
    /* Physical Address */
  }
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Full Physical Address (English)
              </label>
              <input
    type="text"
    value={siteSettings.address}
    onChange={(e) => setSiteSettings({ ...siteSettings, address: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                পূর্ণাঙ্গ ঠিকানা (বাংলা)
              </label>
              <input
    type="text"
    value={siteSettings.address_bn}
    onChange={(e) => setSiteSettings({ ...siteSettings, address_bn: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-bengali focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>
          </div>

          {
    /* Phones & Contacts */
  }
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Office Phone *
              </label>
              <input
    type="text"
    value={siteSettings.phone}
    onChange={(e) => setSiteSettings({ ...siteSettings, phone: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Emergency Phone
              </label>
              <input
    type="text"
    value={siteSettings.emergency_phone}
    onChange={(e) => setSiteSettings({ ...siteSettings, emergency_phone: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                WhatsApp Hotline
              </label>
              <input
    type="text"
    value={siteSettings.whatsapp}
    onChange={(e) => setSiteSettings({ ...siteSettings, whatsapp: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Office Email *
              </label>
              <input
    type="email"
    value={siteSettings.email}
    onChange={(e) => setSiteSettings({ ...siteSettings, email: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>
          </div>

          {
    /* Footer About Text */
  }
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Footer Brand Description (English)
              </label>
              <textarea
    rows={2}
    value={siteSettings.footer_about_text || ""}
    onChange={(e) => setSiteSettings({ ...siteSettings, footer_about_text: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                ফুটার মাদ্রাসা পরিচিতি টেক্সট (বাংলা)
              </label>
              <textarea
    rows={2}
    value={siteSettings.footer_about_text_bn || ""}
    onChange={(e) => setSiteSettings({ ...siteSettings, footer_about_text_bn: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-bengali focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          {canEdit && <button
    type="button"
    onClick={() => handleSaveCampusOffice()}
    className="px-5 py-2 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer"
  >
              <Save className="w-4 h-4" />
              <span>Save Campus Office & Footer</span>
            </button>}
        </div>
      </div>

      {
    /* ------------------------------------------------------------- */
  }
      {
    /* ADD / EDIT TEACHER MODAL                                      */
  }
      {
    /* ------------------------------------------------------------- */
  }
      {isAddingTeacher && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl p-6 sm:p-8 border border-stone-200 space-y-5 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-stone-200 pb-3">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-amber-700" />
                  <span>{editingTeacher ? "Edit Teacher Profile" : "Add New Faculty Member"}</span>
                </h3>
                <p className="text-[11px] text-slate-500">
                  Update teacher credentials, designations, and photograph for the About page.
                </p>
              </div>
              <button
    type="button"
    onClick={() => {
      setIsAddingTeacher(false);
      setEditingTeacher(null);
    }}
    className="p-1 rounded-lg text-slate-400 hover:text-slate-700 cursor-pointer"
  >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveTeacherModalSubmit} className="space-y-4">
              {
    /* Photo Upload & Preview */
  }
              <div className="p-4 bg-stone-50 rounded-xl border border-stone-200 space-y-3">
                <label className="block text-xs font-bold text-slate-800">
                  Teacher Profile Photo *
                </label>

                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <div className="w-20 h-20 rounded-2xl overflow-hidden bg-slate-900 shrink-0 border-2 border-white shadow-md relative">
                    <img
    src={resolveImageUrl(teacherFormData.photo)}
    alt="Preview"
    className="w-full h-full object-cover"
    referrerPolicy="no-referrer"
    onError={(e) => {
      e.currentTarget.src = DEFAULT_FALLBACK_IMAGE;
    }}
  />
                  </div>

                  <div className="flex-1 space-y-2 w-full">
                    <div
    onClick={() => teacherPhotoInputRef.current?.click()}
    className="border border-dashed border-stone-300 hover:border-emerald-700 bg-white rounded-lg p-2.5 text-center cursor-pointer transition-colors"
  >
                      <div className="flex items-center justify-center gap-2 text-xs text-slate-700">
                        <UploadCloud className="w-4 h-4 text-emerald-800" />
                        <span className="font-semibold text-[11px]">
                          Upload new photo or <span className="text-emerald-800 underline">browse file</span>
                        </span>
                      </div>
                    </div>

                    {
    /* Quick Preset Avatars */
  }
                    <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
                      <span className="text-slate-500 font-medium">Quick Avatars:</span>
                      {PRESET_TEACHER_PHOTOS.map((pr, idx) => <button
    key={idx}
    type="button"
    onClick={() => setTeacherFormData((prev) => ({ ...prev, photo: pr.path }))}
    className={`px-2 py-0.5 rounded border transition-colors cursor-pointer ${teacherFormData.photo === pr.path ? "bg-emerald-900 text-white border-emerald-950 font-bold" : "bg-white text-slate-700 border-stone-200 hover:bg-stone-100"}`}
  >
                          {pr.name.split("/")[0]}
                        </button>)}
                    </div>

                    <input
    type="file"
    accept="image/*"
    ref={teacherPhotoInputRef}
    onChange={handleTeacherPhotoFileChange}
    className="hidden"
  />

                    <input
    type="text"
    value={teacherFormData.photo || ""}
    onChange={(e) => setTeacherFormData({ ...teacherFormData, photo: e.target.value })}
    placeholder="Or paste photo URL / path (/assets/images/...)"
    className="w-full px-3 py-1.5 text-[11px] rounded-lg border border-stone-200 bg-white font-mono"
  />
                  </div>
                </div>
              </div>

              {
    /* Names */
  }
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Teacher Full Name (English) *
                  </label>
                  <input
    type="text"
    required
    value={teacherFormData.name || ""}
    onChange={(e) => setTeacherFormData({ ...teacherFormData, name: e.target.value })}
    placeholder="e.g. Maulana Hafiz Mohammad Abdullah"
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    শিক্ষকের পুরো নাম (বাংলা) *
                  </label>
                  <input
    type="text"
    required
    value={teacherFormData.name_bn || ""}
    onChange={(e) => setTeacherFormData({ ...teacherFormData, name_bn: e.target.value })}
    placeholder="যেমন: মাওলানা হাফেজ মোহাম্মদ আব্দুল্লাহ"
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-bengali focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
                </div>
              </div>

              {
    /* Designation */
  }
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Designation / Title (English) *
                  </label>
                  <input
    type="text"
    required
    value={teacherFormData.designation || ""}
    onChange={(e) => setTeacherFormData({ ...teacherFormData, designation: e.target.value })}
    placeholder="e.g. Principal & Head of Institution"
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    পদবী ও দায়িত্ব (বাংলা) *
                  </label>
                  <input
    type="text"
    required
    value={teacherFormData.designation_bn || ""}
    onChange={(e) => setTeacherFormData({ ...teacherFormData, designation_bn: e.target.value })}
    placeholder="যেমন: অধ্যক্ষ ও মুহতামিম"
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-bengali focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
                </div>
              </div>

              {
    /* Department */
  }
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Department (English) *
                  </label>
                  <select
    value={teacherFormData.department || "Islamic Studies"}
    onChange={(e) => setTeacherFormData({ ...teacherFormData, department: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 bg-white"
  >
                    <option value="Administration & Hifz">Administration & Hifz</option>
                    <option value="Islamic Studies & Arabic">Islamic Studies & Arabic</option>
                    <option value="Noorani Foundation">Noorani Foundation</option>
                    <option value="Languages">Languages (Arabic, English, Farsi)</option>
                    <option value="General Primary (English & Math)">General Primary (English & Math)</option>
                    <option value="Tahfizul Quran Track">Tahfizul Quran Track</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    বিভাগ (বাংলা) *
                  </label>
                  <input
    type="text"
    value={teacherFormData.department_bn || ""}
    onChange={(e) => setTeacherFormData({ ...teacherFormData, department_bn: e.target.value })}
    placeholder="যেমন: প্রশাসন ও তাহফিজুল কুরআন"
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-bengali"
  />
                </div>
              </div>

              {
    /* Qualification */
  }
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Qualifications & Sanad (English)
                  </label>
                  <input
    type="text"
    value={teacherFormData.qualification || ""}
    onChange={(e) => setTeacherFormData({ ...teacherFormData, qualification: e.target.value })}
    placeholder="e.g. Dawra-e-Hadith, Hafez-e-Quran"
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300"
  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    শিক্ষাগত যোগ্যতা ও সনদ (বাংলা)
                  </label>
                  <input
    type="text"
    value={teacherFormData.qualification_bn || ""}
    onChange={(e) => setTeacherFormData({ ...teacherFormData, qualification_bn: e.target.value })}
    placeholder="যেমন: দাওরায়ে হাদিস, হাফেজে কুরআন"
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-bengali"
  />
                </div>
              </div>

              {
    /* Bio */
  }
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Short Bio (English)
                  </label>
                  <textarea
    rows={2}
    value={teacherFormData.bio || ""}
    onChange={(e) => setTeacherFormData({ ...teacherFormData, bio: e.target.value })}
    placeholder="Short summary of teaching experience..."
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300"
  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    সংক্ষিপ্ত পরিচিতি ও অভিজ্ঞতা (বাংলা)
                  </label>
                  <textarea
    rows={2}
    value={teacherFormData.bio_bn || ""}
    onChange={(e) => setTeacherFormData({ ...teacherFormData, bio_bn: e.target.value })}
    placeholder="শিক্ষাদানের অভিজ্ঞতা ও সংক্ষিপ্ত বার্তা..."
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-bengali"
  />
                </div>
              </div>

              {
    /* Contact Info */
  }
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Contact Phone (Optional)
                  </label>
                  <input
    type="text"
    value={teacherFormData.phone || ""}
    onChange={(e) => setTeacherFormData({ ...teacherFormData, phone: e.target.value })}
    placeholder="+880 1819-000000"
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-mono"
  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Email Address (Optional)
                  </label>
                  <input
    type="email"
    value={teacherFormData.email || ""}
    onChange={(e) => setTeacherFormData({ ...teacherFormData, email: e.target.value })}
    placeholder="teacher@baitulmuqaddas.edu.bd"
    className="w-full px-3 py-2 text-xs rounded-xl border border-stone-300 font-mono"
  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-stone-200">
                <button
    type="button"
    onClick={() => {
      setIsAddingTeacher(false);
      setEditingTeacher(null);
    }}
    className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-stone-100 rounded-xl cursor-pointer"
  >
                  Cancel
                </button>
                <button
    type="submit"
    className="px-5 py-2 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-xl shadow-xs cursor-pointer"
  >
                  {editingTeacher ? "Update Teacher" : "Add Teacher"}
                </button>
              </div>
            </form>
          </div>
        </div>}

      {
    /* ------------------------------------------------------------- */
  }
      {
    /* SAVING / LOADING MODAL                                        */
  }
      {
    /* ------------------------------------------------------------- */
  }
      {isSavingModalOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-emerald-200 max-w-sm w-full text-center space-y-4 animate-in zoom-in-95">
            <div className="relative w-16 h-16 mx-auto flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border-4 border-emerald-100 border-t-emerald-800 animate-spin" />
              <Database className="w-6 h-6 text-emerald-800" />
            </div>
            
            <div className="space-y-1">
              <h3 className="text-base font-bold text-slate-900 tracking-tight">
                {savingModalTitle}
              </h3>
              <p className="text-xs text-slate-500">
                {savingModalSubtitle}
              </p>
            </div>

            <div className="flex items-center justify-center gap-1.5 text-[11px] font-semibold text-emerald-800 bg-emerald-50 py-2 px-3 rounded-xl border border-emerald-200/80">
              <Sparkles className="w-3.5 h-3.5 animate-pulse text-emerald-600" />
              <span>Saving & Syncing to MySQL Database...</span>
            </div>
          </div>
        </div>}
    </div>;
};
